import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Mail, Loader2, CheckCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

const NewsletterSignup = () => {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: "Email Required",
        description: "Please enter your email address.",
        variant: "destructive",
      });
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase.functions.invoke('mailchimp-subscribe', {
        body: { email, source: 'newsletter_footer' }
      });

      if (error) throw error;

      setIsSuccess(true);
      setEmail("");
      toast({
        title: "Subscribed!",
        description: "You've been added to our newsletter.",
      });

      // Reset success state after 3 seconds
      setTimeout(() => setIsSuccess(false), 3000);
    } catch (error) {
      console.error("Newsletter signup error:", error);
      toast({
        title: "Signup Failed",
        description: "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-primary-foreground/5 rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <Mail className="h-4 w-4" />
        <h4 className="font-semibold text-sm">Stay Updated</h4>
      </div>
      <p className="text-xs text-primary-foreground/70 mb-3">
        Get payment processing tips and industry news.
      </p>
      <form onSubmit={handleSubmit} className="flex gap-2">
        <Input
          type="email"
          placeholder="your@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          className="h-9 text-sm bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/50"
          disabled={isLoading || isSuccess}
        />
        <Button 
          type="submit" 
          size="sm" 
          variant="secondary"
          disabled={isLoading || isSuccess}
          className="h-9 px-3"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : isSuccess ? (
            <CheckCircle className="h-4 w-4" />
          ) : (
            "Join"
          )}
        </Button>
      </form>
      <p className="text-xs text-primary-foreground/50 mt-2">
        Join 500+ South Florida business owners
      </p>
    </div>
  );
};

export default NewsletterSignup;
